import { invoiceDaysPastDue } from '@packages/types';
const DISPLAY_STATUS_KEYS = {
    DRAFT: 'invoice:status.draft',
    APPROVED: 'invoice:status.approved',
    SENT: 'invoice:status.sent',
    PAID: 'invoice:status.paid',
    VOIDED: 'invoice:status.voided',
    PRO_FORMA_SENT: 'invoice:status.proFormaSent',
    WRITTEN_OFF: 'invoice:status.writtenOff',
    CREDITED: 'invoice:status.credited',
    PARTIALLY_PAID: 'invoice:status.partiallyPaid',
    OVERDUE: 'invoice:status.overdue',
    OVERDUE_NOT_SENT: 'invoice:status.overdueNotSent',
};
/**
 * The single rule for how an invoice's status reads, shared by the Bill
 * invoices list and the invoice list PDF and spreadsheet so the screen and the
 * files never disagree. "Paid" always means cash was received: an invoice
 * covered wholly by a write-off, or wholly by credit notes, with no cash
 * settled, reads "Written off" or "Credited" instead. A partial payment shows
 * on APPROVED as well as SENT, because the backend accepts payments on both.
 * An invoice still owing past its effective due date reads "Overdue", ahead of
 * "Partially paid": the overdue balance is what needs acting on, and the
 * amount columns still show what has been paid. An overdue invoice still
 * APPROVED reads "Overdue – not sent", so the reading never hides that the
 * client may not have it: that one needs sending, not chasing.
 * Soft-deleted rows are the caller's to label, with `invoice:status.deleted`:
 * an archived draft keeps its DRAFT status underneath.
 */
export function describeInvoiceStatus(copy, facts) {
    const status = resolveInvoiceDisplayStatus(facts);
    return { status, label: copy(DISPLAY_STATUS_KEYS[status]) };
}
/** The bare lifecycle label ("Sent"), for surfaces naming a status rather than an invoice, such as a filter heading. */
export function invoiceLifecycleStatusLabel(copy, status) {
    return copy(DISPLAY_STATUS_KEYS[status]);
}
/** The display status alone, for a surface that styles by it rather than printing the label. */
export function resolveInvoiceDisplayStatus({ status, paymentStatus, settledAmountMinorUnits = 0, creditedAmountMinorUnits = 0, writtenOffAmountMinorUnits = 0, totalMinorUnits = 0, liveProforma, dueDate, date, asOf = new Date(), }) {
    if (status === 'DRAFT' && liveProforma)
        return 'PRO_FORMA_SENT';
    const coveredWithoutCash = paymentStatus === 'PAID' && settledAmountMinorUnits === 0 && totalMinorUnits > 0;
    if (coveredWithoutCash && writtenOffAmountMinorUnits >= totalMinorUnits)
        return 'WRITTEN_OFF';
    if (coveredWithoutCash && creditedAmountMinorUnits >= totalMinorUnits)
        return 'CREDITED';
    const awaitingPayment = (status === 'SENT' || status === 'APPROVED') && paymentStatus !== undefined && paymentStatus !== 'PAID';
    if (awaitingPayment && dueDate !== undefined && date !== undefined && invoiceDaysPastDue({ dueDate, date }, asOf) > 0) {
        return status === 'APPROVED' ? 'OVERDUE_NOT_SENT' : 'OVERDUE';
    }
    if (awaitingPayment && paymentStatus === 'PARTIALLY_PAID')
        return 'PARTIALLY_PAID';
    return status;
}
/** Whether a display status is either Overdue reading, for surfaces that act on overdue regardless of whether it was sent. */
export function isOverdueDisplayStatus(status) {
    return status === 'OVERDUE' || status === 'OVERDUE_NOT_SENT';
}
